import { useCallback, useMemo } from "react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";

import { type Cohort, FEATURE_FLAG_ID } from "..";
import { type Product } from "../../product-subnav";
import { type CCPKey } from "../common/constants";

export function useAnalytics({ isMobile }: { isMobile?: boolean }) {
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const commonProps = useMemo(
        () => ({
            product: "marketing"
        }),
        []
    );

    const fireFeatureExposed = useCallback(
        (cohort: Cohort) => {
            createAnalyticsEvent({
                ...commonProps,
                eventType: "operational",
                action: "exposed",
                actionSubject: "feature",
                attributes: {
                    flagKey: FEATURE_FLAG_ID,
                    value: cohort,
                    growthInitiative: "cross-flow",
                    isMobile
                }
            }).fire();
        },
        [commonProps, createAnalyticsEvent, isMobile]
    );

    const fireCardClicked = useCallback(
        (
            eventContainer:
                | "ProductsRecommendationsContainer"
                | "NewProductsContainer",
            product: Product
        ) => {
            createAnalyticsEvent({
                ...commonProps,
                eventType: "ui",
                action: "clicked",
                actionSubject: "linkButton",
                attributes: {
                    productKey: product.id,
                    eventContainer: eventContainer,
                    isMobile
                }
            }).fire();
        },
        [commonProps, createAnalyticsEvent, isMobile]
    );

    const fireGlobalNavProductRecommendationsViewed = useCallback(
        (recommendations: string[]) => {
            createAnalyticsEvent({
                ...commonProps,
                eventType: "screen",
                name: "productNavRecommendation",
                attributes: {
                    recommendedProductKeys: recommendations,
                    isMobile
                }
            }).fire();
        },
        [commonProps, createAnalyticsEvent, isMobile]
    );

    const fireFetchGrowthRecommendationSucceeded = useCallback(
        (recommendedProductIds: CCPKey[], elapsedTimeMiliseconds: number) => {
            createAnalyticsEvent({
                ...commonProps,
                eventType: "operational",
                actionSubject: "fetchGrowthRecommendation",
                action: "succeeded",
                attributes: {
                    recommendedProductIds,
                    elapsedTime: elapsedTimeMiliseconds / 1000,
                    isMobile
                }
            }).fire();
        },
        [commonProps, createAnalyticsEvent, isMobile]
    );

    const fireFetchGrowthRecommendationFailed = useCallback(
        (error: unknown, elapsedTimeMiliseconds: number) => {
            createAnalyticsEvent({
                ...commonProps,
                eventType: "operational",
                actionSubject: "fetchGrowthRecommendation",
                action: "failed",
                attributes: {
                    errorMessage: (error as Error)?.message || error,
                    elapsedTime: elapsedTimeMiliseconds / 1000,
                    isMobile
                }
            }).fire();
        },
        [commonProps, createAnalyticsEvent, isMobile]
    );

    const fireRenderGlobalNavProductRecommendationsFailed = useCallback(
        (error: Error) => {
            createAnalyticsEvent({
                ...commonProps,
                eventType: "operational",
                actionSubject: "renderGlobalNavProductRecommendations",
                action: "failed",
                attributes: {
                    errorMessage: error?.message || error,
                    isMobile
                }
            }).fire();
        },
        [commonProps, createAnalyticsEvent, isMobile]
    );

    return {
        fireFeatureExposed,
        fireCardClicked,
        fireGlobalNavProductRecommendationsViewed,
        fireFetchGrowthRecommendationSucceeded,
        fireFetchGrowthRecommendationFailed,
        fireRenderGlobalNavProductRecommendationsFailed
    };
}
